import { useEffect, useState } from 'react';
import { List } from '@refinedev/antd';
import { Table, Switch, Button, Space, App as AntApp, Card, Skeleton, Tag, Row, Col, Statistic } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, CarOutlined, SwapOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

interface TariffConfig {
  id: number;
  tariff: 'ECONOM' | 'STANDARD' | 'COMFORT' | 'BUSINESS' | 'SEVEN_PLUS';
  isActive: boolean;
  sortOrder: number;
  carCount: number;
  tripCount: number;
}

const TARIFF_LABELS: Record<string, string> = {
  ECONOM: 'Ekonom',
  STANDARD: 'Standart',
  COMFORT: 'Komfort',
  BUSINESS: 'Biznes',
  SEVEN_PLUS: '7+',
};

const TARIFF_COLORS: Record<string, string> = {
  ECONOM: 'default',
  STANDARD: 'blue',
  COMFORT: 'cyan',
  BUSINESS: 'purple',
  SEVEN_PLUS: 'gold',
};

const CARD_ACCENT: Record<string, string> = {
  ECONOM: '#7F8C8D',
  STANDARD: '#1677FF',
  COMFORT: '#13C2C2',
  BUSINESS: '#722ED1',
  SEVEN_PLUS: '#D4AD00',
};

export const TariffList = () => {
  const { message } = AntApp.useApp();
  const [rows, setRows] = useState<TariffConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<TariffConfig[]>('/admin/tariffs');
      setRows(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('tariffs.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (row: TariffConfig, isActive: boolean) => {
    setBusyId(row.id);
    try {
      await apiClient.put(`/admin/tariffs/${row.id}`, { isActive });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive } : r)));
      message.success(t('tariffs.saved'));
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('tariffs.saveFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const reordered = [...rows];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setRows(reordered);
    try {
      const res = await apiClient.put<TariffConfig[]>('/admin/tariffs/reorder', {
        orderedIds: reordered.map((r) => r.id),
      });
      setRows(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('tariffs.saveFailed'));
      load();
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: unknown, __: TariffConfig, idx: number) => idx + 1,
    },
    {
      title: t('tariffs.name'),
      dataIndex: 'tariff',
      key: 'tariff',
      render: (tariff: TariffConfig['tariff']) => (
        <Space>
          <Tag color={TARIFF_COLORS[tariff]}>{TARIFF_LABELS[tariff] || tariff}</Tag>
          <span style={{ color: '#7F8C8D', fontSize: 12 }}>{tariff}</span>
        </Space>
      ),
    },
    {
      title: t('tariffs.cars'),
      dataIndex: 'carCount',
      key: 'carCount',
      width: 100,
      render: (n: number) => <span>{n}</span>,
    },
    {
      title: t('tariffs.trips'),
      dataIndex: 'tripCount',
      key: 'tripCount',
      width: 100,
      render: (n: number) => <span>{n}</span>,
    },
    {
      title: t('tariffs.available'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      render: (isActive: boolean, row: TariffConfig) => (
        <Switch
          checked={isActive}
          loading={busyId === row.id}
          onChange={(v) => toggleActive(row, v)}
        />
      ),
    },
    {
      title: t('tariffs.order'),
      key: 'order',
      width: 140,
      render: (_: unknown, _row: TariffConfig, idx: number) => (
        <Space>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={idx === 0}
            onClick={() => move(idx, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={idx === rows.length - 1}
            onClick={() => move(idx, 1)}
          />
        </Space>
      ),
    },
  ];

  return (
    <List title={t('tariffs.title')}>
      {/* Stat cards — one per tariff, shows car and trip counts */}
      {rows.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {rows.map((row) => {
            const accent = CARD_ACCENT[row.tariff] || '#1677FF';
            return (
              <Col key={row.id} xs={24} sm={12} md={8} lg={Math.max(4, Math.floor(24 / rows.length))}>
                <Card
                  bordered
                  style={{
                    borderTop: `3px solid ${accent}`,
                    opacity: row.isActive ? 1 : 0.55,
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space>
                      <Tag color={TARIFF_COLORS[row.tariff]} style={{ margin: 0 }}>
                        {TARIFF_LABELS[row.tariff] || row.tariff}
                      </Tag>
                      {!row.isActive && (
                        <Tag color="red" style={{ margin: 0 }}>
                          {t('tariffs.inactive')}
                        </Tag>
                      )}
                    </Space>
                    <Row gutter={8} style={{ marginTop: 8 }}>
                      <Col span={12}>
                        <Statistic
                          title={
                            <Space size={4}>
                              <CarOutlined />
                              {t('tariffs.cars')}
                            </Space>
                          }
                          value={row.carCount}
                          valueStyle={{ fontSize: 22, fontWeight: 600, color: accent }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={
                            <Space size={4}>
                              <SwapOutlined />
                              {t('tariffs.trips')}
                            </Space>
                          }
                          value={row.tripCount}
                          valueStyle={{ fontSize: 22, fontWeight: 600, color: accent }}
                        />
                      </Col>
                    </Row>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={
          loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 4 }} /></Card> }
            : false
        }
      />
    </List>
  );
};
